import { lessThanOrEqual } from "dinero.js";
import { Immutable } from "immer";
import React, { useCallback, useEffect, useState } from "react";
import { FinancingModalTriggerBannerMinicart } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/components/Link/index";
import { SrFootnote } from "tsi-common-react/src/components/SrFootnote/index";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { useAppSelector } from "tsi-common-react/src/store/reducers";
import {
    filterFinancingPlans,
    getLongestFinancingPlan,
    listFinancingPlans,
} from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import {
    type Dinero,
    getDinero,
    getMonthlyPrice,
    toUnit,
} from "tsi-common-react/src/utils/money";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { jt, t } from "ttag";

type IProps = Immutable<{}> & {
    closeModal: () => void;
};

// TODO: Consolidate Financing Trigger components into one
export const FinancingModalTriggerMinicart = (props: IProps) => {
    const [termLength, setTermLength] = useState("1");
    const [termSuperscript, setTermSuperscript] = useState("");
    const [termThreshold, setTermThreshold] = useState("");

    const financeURL = usePageURL("finance_link");
    const financingPlans = useAppSelector(
        (state) => state.checkout.data.financing_plans,
    );
    const basketData = useAppSelector((state) => state.checkout.data.basket);
    const pricePerMonth: Dinero = basketData
        ? getMonthlyPrice(
              getDinero(basketData.total_excl_tax),
              parseInt(termLength, 10),
          )
        : getDinero(0.0);
    const showFinancing: boolean =
        basketData && termThreshold
            ? parseFloat(basketData.total_excl_tax) >= parseFloat(termThreshold)
            : false;

    const calcFinancePlan = useCallback(() => {
        const longestPlan = getLongestFinancingPlan(
            filterFinancingPlans(financingPlans),
        );
        if (longestPlan && longestPlan.term_months > isoMonths.wrap(0)) {
            setTermLength(longestPlan.term_months.toString());
            setTermSuperscript(
                longestPlan.fine_print_superscript
                    ? longestPlan.fine_print_superscript
                    : "",
            );
            setTermThreshold(
                longestPlan.product_price_threshold
                    ? longestPlan.product_price_threshold
                    : "",
            );
        } else {
            const total = basketData
                ? getDinero(basketData.total_excl_tax)
                : getDinero(0);

            const plans = listFinancingPlans();
            const availablePlans = plans.filter((plan) => {
                return lessThanOrEqual(plan.threshold, total);
            });

            if (availablePlans.length) {
                const bestPlan = availablePlans[availablePlans.length - 1];

                setTermLength(isoMonths.unwrap(bestPlan.length).toString());
                setTermSuperscript(bestPlan.superscript);
                setTermThreshold(toUnit(bestPlan.threshold).toString());
            }
        }
    }, [basketData, financingPlans]);

    useEffect(() => {
        calcFinancePlan();
    }, [calcFinancePlan]);

    return (
        <div className="basket-popover-financing">
            {showFinancing && (
                <>
                    <div className="basket-popover-financing__title">
                        {t`or Special Financing`}
                    </div>
                    <div className="basket-popover-financing__terms">
                        {(() => {
                            const price = format.money(pricePerMonth);
                            const sup2 = (
                                <sup key="sup2">
                                    <Link href={financeURL}>
                                        2
                                        <SrFootnote
                                            text={t`Additional information about Terms and Conditions`}
                                        />
                                    </Link>
                                </sup>
                            );
                            const forText = (
                                <span
                                    key="for"
                                    className="basket-popover-financing__terms--medium"
                                >
                                    {t`for`}
                                </span>
                            );
                            const sup1 = (
                                <sup key="sup1">
                                    <Link href={financeURL}>
                                        {termSuperscript}
                                        <SrFootnote
                                            text={t`Additional information about Terms and Conditions`}
                                        />
                                    </Link>
                                </sup>
                            );
                            return jt`${price}/mo${sup2} ${forText} ${termLength} Months${sup1}`;
                        })()}
                    </div>
                </>
            )}
            <FinancingModalTriggerBannerMinicart
                closeModal={props.closeModal}
            />
        </div>
    );
};
